import React from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Smartphone, Monitor, LayoutGrid, Image as ImageIcon, Search } from "lucide-react";
import { SectionContainer, SectionLabel, SectionHeading, SectionLead } from "./SectionPrimitives";

const FORMATS = [
  {
    id: "search",
    icon: Search,
    label: "Search Card",
    image: "/screenshots/01_simulator_search_card.webp",
    desc: "Die Standard-Auslieferung in der Google-Suche — Anzeige inkl. Preis, Standort, Bewertung und CTA. Pflichtfelder und Schema müssen sitzen.",
    bullets: ["Vehicle Schema · Offer", "Bilder optimiert", "Lokaler Standort + Rating"],
  },
  {
    id: "carousel",
    icon: LayoutGrid,
    label: "Carousel",
    image: "/screenshots/02_simulator_carousel.webp",
    desc: "Mehrere Fahrzeuge im Karussell-Layout, ideal für ähnliche Modelle aus Ihrem Bestand. Bringt Cross-Selling und Wiedererkennung.",
    bullets: ["3–10 Slots pro Anzeige", "Marken-/Modell-Cluster", "Höhere CTR durch Auswahl"],
  },
  {
    id: "mobile",
    icon: Smartphone,
    label: "Mobile",
    image: "/screenshots/03_simulator_mobile.webp",
    desc: "Mobile-First-Auslieferung — kompaktes Layout, fokussierter Conversion-Button. Über 70% der Käufer recherchieren am Smartphone.",
    bullets: ["Mobile-First-Bild", "Tap-to-Call optional", "Reduzierte Texte"],
  },
  {
    id: "desktop",
    icon: Monitor,
    label: "Desktop",
    image: "/screenshots/04_simulator_desktop.webp",
    desc: "Volle Google-SERP mit Anzeigen-Block und ähnlichen Fahrzeugen unterhalb. Idealer Funnel-Einstieg für vergleichende Käufer.",
    bullets: ["SERP-Topplatzierung", "Vergleichs-Anzeigen", "Branded + Generic Mix"],
  },
  {
    id: "display",
    icon: ImageIcon,
    label: "Display",
    image: "/screenshots/05_simulator_display.webp",
    desc: "Visuelle Wiederausspielung im Display-Netzwerk und auf Auto-Portalen. Re-Targeting für nicht abgeschlossene Funnel.",
    bullets: ["Dynamic Display", "Re-Targeting-Listen", "Brand-Boost"],
  },
];

const INTERVAL = 3500;

export default function AdFormats() {
  const [active, setActive] = React.useState(FORMATS[0].id);
  const [paused, setPaused] = React.useState(false);
  const pauseTimer = React.useRef(null);

  React.useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setActive((cur) => {
        const idx = FORMATS.findIndex((f) => f.id === cur);
        return FORMATS[(idx + 1) % FORMATS.length].id;
      });
    }, INTERVAL);
    return () => clearInterval(id);
  }, [paused]);

  const handleUserSelect = (val) => {
    setActive(val);
    setPaused(true);
    clearTimeout(pauseTimer.current);
    pauseTimer.current = setTimeout(() => setPaused(false), 8000);
  };

  const activeIdx = FORMATS.findIndex((f) => f.id === active);

  return (
    <SectionContainer id="anzeigen-simulator" className="bg-white">
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <SectionLabel number="/ 06" label="Anzeigen-Formate" />
          <SectionHeading>Jedes Fahrzeug. Jedes Format. Live im Simulator.</SectionHeading>
          <SectionLead>
            Wir orchestrieren Ihre Anzeigen über alle Google-Formate hinweg — von der klassischen
            Search-Card bis zur Display-Re-Auslieferung. Die Vorschau zeigt exakt, was Käufer sehen.
          </SectionLead>
        </div>
        <div className="lg:col-span-5">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
            <p className="label-mono text-slate-500">Format-Coverage</p>
            <div className="mt-3 grid grid-cols-5 gap-2">
              {FORMATS.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={f.id} className={`aspect-square rounded-md border bg-white flex items-center justify-center transition-colors ${i === activeIdx ? "border-cyan-400 text-cyan-600" : "border-slate-200 text-cyan-600 hover:border-cyan-300"}`}>
                    <Icon className="size-4" />
                  </div>
                );
              })}
            </div>
            <p className="mt-3 font-mono text-[11px] text-slate-500">5 Formate · 1 Datenquelle · 0 manueller Aufwand</p>
          </div>
        </div>
      </div>

      <div className="mt-14">
        <Tabs value={active} onValueChange={handleUserSelect} className="w-full">
          <TabsList data-testid="ad-formats-tabs" className="bg-white border border-slate-200 rounded-lg p-1 h-auto flex flex-wrap gap-1">
            {FORMATS.map((f) => {
              const Icon = f.icon;
              const isActive = f.id === active;
              return (
                <TabsTrigger
                  key={f.id}
                  value={f.id}
                  data-testid={`tab-${f.id}`}
                  data-track="ad_format_tab"
                  data-format={f.id}
                  className="relative data-[state=active]:bg-slate-900 data-[state=active]:text-white text-slate-600 rounded-md px-4 py-2.5 text-sm font-medium inline-flex items-center gap-2"
                >
                  <Icon className="size-3.5" />
                  {f.label}
                  {isActive && !paused && (
                    <span
                      key={active}
                      className="absolute bottom-1 left-1 right-1 h-0.5 rounded-full bg-cyan-400"
                      style={{ animation: `tabProgress ${INTERVAL}ms linear forwards` }}
                    />
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {FORMATS.map((f) => (
            <TabsContent key={f.id} value={f.id} className="mt-6">
              <div className="grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-8 relative bg-white border border-slate-200 rounded-lg overflow-hidden ring-soft">
                  <div className="flex items-center justify-between px-4 h-9 border-b border-slate-200 bg-slate-50/60">
                    <div className="flex items-center gap-1.5">
                      <span className="size-2.5 rounded-full bg-slate-300" />
                      <span className="size-2.5 rounded-full bg-slate-300" />
                      <span className="size-2.5 rounded-full bg-slate-300" />
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      app.adsfux.io · simulator / {f.id}
                    </div>
                    <div className="w-12" />
                  </div>
                  <img
                    src={f.image}
                    alt={`${f.label} Anzeigen-Vorschau`}
                    className="w-full h-auto block"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="lg:col-span-4">
                  <div className="bg-white border border-slate-200 rounded-lg p-6 h-full">
                    <p className="label-mono text-slate-500">{f.label}</p>
                    <h3 className="mt-3 font-display text-xl font-semibold text-slate-900">
                      Format · {f.label}
                    </h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                    <ul className="mt-5 space-y-2">
                      {f.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-[13px] text-slate-700">
                          <span className="mt-1.5 size-1.5 rounded-full bg-cyan-500 shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <style>{`
        @keyframes tabProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </SectionContainer>
  );
}
